/**
 * WorldTravelScene — Main travel scene with parallax scrolling,
 * caravan movement, day/night cycle, region transitions, and encounters.
 *
 * PixiJS handles the game world rendering (parallax layers, caravan sprites).
 * All HUD and dialog overlays are HTML/CSS in #ui-root.
 *
 * @module WorldTravelScene
 */

import { Container, Graphics } from 'pixi.js';
import debugLog from '../debug/DebugLog.js';
import sceneManager from '../core/SceneManager.js';
import eventBus from '../core/EventBus.js';
import focusManager from '../input/FocusManager.js';
import inputManager from '../input/InputManager.js';
import ParallaxRenderer from '../renderer/ParallaxRenderer.js';
import travelSystem from '../systems/TravelSystem.js';
import dataStore from '../systems/DataLoader.js';
import inventorySystem from '../systems/InventorySystem.js';
import questSystem from '../systems/QuestSystem.js';
import InventoryPanel from '../ui/components/InventoryPanel.js';
import QuestLogPanel from '../ui/components/QuestLogPanel.js';
import QuestTracker from '../ui/components/QuestTracker.js';

/** Biome color palettes for procedural backgrounds. */
const BIOME_PALETTES = {
  temperate_forest: {
    sky:        [0x88bbee, 0x5599cc],
    clouds:     0xddddee,
    mountains:  [0x446633, 0x335522],
    trees:      [0x2d5a27, 0x3a7a30, 0x1e4a1a],
    ground:     0x4a7a3a,
    path:       0x8b7355,
    grass:      0x3a6a2a,
  },
  grasslands: {
    sky:        [0xaaccee, 0x88aadd],
    clouds:     0xeeeeff,
    mountains:  [0x887755, 0x776644],
    trees:      [0x6a8a4a, 0x7a9a5a, 0x5a7a3a],
    ground:     0x7a9a50,
    path:       0xa09070,
    grass:      0x6a9a40,
  },
  mountain: {
    sky:        [0x7799bb, 0x5577aa],
    clouds:     0xccccdd,
    mountains:  [0x665544, 0x554433],
    trees:      [0x3a5a3a, 0x2a4a2a, 0x4a6a4a],
    ground:     0x665544,
    path:       0x998877,
    grass:      0x4a6a3a,
  },
  swamp: {
    sky:        [0x778899, 0x556677],
    clouds:     0x99aabb,
    mountains:  [0x445544, 0x334433],
    trees:      [0x2a4a2a, 0x1a3a2a, 0x3a5a3a],
    ground:     0x3a4a30,
    path:       0x665544,
    grass:      0x2a4a20,
  },
  desert: {
    sky:        [0xddbb88, 0xccaa77],
    clouds:     0xeeddcc,
    mountains:  [0xaa8855, 0x997744],
    trees:      [0x8a7a3a, 0x7a6a2a, 0x9a8a4a],
    ground:     0xccaa66,
    path:       0xbb9955,
    grass:      0xaa9944,
  },
  tundra: {
    sky:        [0xaabbcc, 0x8899bb],
    clouds:     0xddeeff,
    mountains:  [0x99aabb, 0x8899aa],
    trees:      [0x3a5a5a, 0x2a4a4a, 0x4a6a6a],
    ground:     0xccdddd,
    path:       0xaabbbb,
    grass:      0xbbcccc,
  },
  dark_forest: {
    sky:        [0x334455, 0x223344],
    clouds:     0x556677,
    mountains:  [0x223322, 0x112211],
    trees:      [0x1a2a1a, 0x0a1a0a, 0x2a3a2a],
    ground:     0x223322,
    path:       0x554433,
    grass:      0x1a2a1a,
  },
  volcanic: {
    sky:        [0x553322, 0x442211],
    clouds:     0x664433,
    mountains:  [0x332211, 0x221100],
    trees:      [0x443322, 0x332211, 0x554433],
    ground:     0x332211,
    path:       0x665533,
    grass:      0x443311,
  },
  magical_heights: {
    sky:        [0x6644aa, 0x5533aa],
    clouds:     0xaa88ee,
    mountains:  [0x5533aa, 0x4422aa],
    trees:      [0x4455aa, 0x3344aa, 0x5566bb],
    ground:     0x443388,
    path:       0x6655aa,
    grass:      0x334488,
  },
  wasteland: {
    sky:        [0x554433, 0x443322],
    clouds:     0x665544,
    mountains:  [0x443322, 0x332211],
    trees:      [0x332211, 0x221100, 0x443322],
    ground:     0x443322,
    path:       0x554433,
    grass:      0x332211,
  },
  demonic: {
    sky:        [0x440011, 0x330000],
    clouds:     0x660022,
    mountains:  [0x330011, 0x220000],
    trees:      [0x441111, 0x331111, 0x551111],
    ground:     0x331111,
    path:       0x552222,
    grass:      0x221111,
  },
  fortress: {
    sky:        [0x221133, 0x110022],
    clouds:     0x332244,
    mountains:  [0x222222, 0x111111],
    trees:      [0x222233, 0x111122, 0x333344],
    ground:     0x222222,
    path:       0x444444,
    grass:      0x1a1a2a,
  },
};

/** Default palette if biome not found. */
const DEFAULT_PALETTE = BIOME_PALETTES.temperate_forest;

/** Tile width for seamless scrolling layers — must exceed max viewport width. */
const TILE_WIDTH = 2000;

/** Ground height as fraction of viewport. */
const GROUND_FRACTION = 0.30;

/** Path height in pixels. */
const PATH_HEIGHT = 40;

export default class WorldTravelScene {
  constructor() {
    this.container = new Container();

    /** @type {ParallaxRenderer|null} */
    this._parallax = null;

    /** @type {HTMLElement|null} */
    this._hudEl = null;

    /** @type {HTMLElement|null} */
    this._dialogEl = null;

    /** Camera X position (world units). */
    this._cameraX = 0;

    /** Viewport dimensions. */
    this._width = 800;
    this._height = 600;

    /** Current biome palette. */
    this._palette = DEFAULT_PALETTE;

    /** Caravan graphics container. */
    this._caravanContainer = null;

    /** Event listener references for cleanup. */
    this._eventHandlers = {};

    /** @type {InventoryPanel|null} Inventory panel instance. */
    this._inventoryPanel = null;

    /** @type {QuestLogPanel|null} Quest log panel instance. */
    this._questLogPanel = null;

    /** @type {QuestTracker|null} Quest tracker HUD widget. */
    this._questTracker = null;
  }

  async init() {
    debugLog.info('WorldTravelScene: init');
  }

  async enter(data) {
    debugLog.event('WorldTravelScene: enter');

    // Get viewport dimensions
    const canvas = document.getElementById('game-canvas');
    if (canvas) {
      this._width = canvas.clientWidth || 800;
      this._height = canvas.clientHeight || 600;
    }

    // Determine starting region
    let startRegion = 'greenhollow';
    if (data && data.regionId) {
      startRegion = data.regionId;
    }

    // Use party from data or from debug
    const party = (data && data.party) || window.__debug?.party || null;

    // Initialize travel system
    travelSystem.init(startRegion, party);

    // Set palette from biome
    const region = travelSystem.currentRegion;
    if (region) {
      this._palette = BIOME_PALETTES[region.biome] || DEFAULT_PALETTE;
    }

    // Re-read dimensions from actual rendered canvas (may differ from initial)
    const canvasEl = document.querySelector('#game-canvas canvas');
    if (canvasEl) {
      this._width = canvasEl.clientWidth || this._width;
      this._height = canvasEl.clientHeight || this._height;
    }

    // Build parallax layers
    this._buildParallax();

    // Position parallax layers immediately so no black flash on first frame
    this._cameraX = travelSystem.position * 100;
    this._parallax.update(this._cameraX);

    // Build caravan visuals
    this._buildCaravan();

    // Build HUD (HTML/CSS)
    this._buildHUD();

    // Subscribe to travel events
    this._subscribeEvents();

    // Register debug commands
    this._registerDebugCommands();

    // Update HUD with initial state
    this._updateHUD();

    // Initialize inventory system with party
    if (party) {
      inventorySystem.init(party);
    }

    // Create inventory panel
    this._inventoryPanel = new InventoryPanel({ party });

    // Listen for inventory hotkey (I key / Y button)
    this._eventHandlers.onInventory = () => {
      if (this._inventoryPanel) {
        this._inventoryPanel.toggle();
      }
    };
    eventBus.on('action:inventory', this._eventHandlers.onInventory);

    // Create quest log panel
    this._questLogPanel = new QuestLogPanel({ party });

    // Listen for quest hotkey (J key / D-pad Up)
    this._eventHandlers.onQuest = () => {
      if (this._questLogPanel) {
        this._questLogPanel.toggle();
      }
    };
    eventBus.on('action:quest', this._eventHandlers.onQuest);

    // Create quest tracker HUD widget
    this._questTracker = new QuestTracker({
      onOpenQuestLog: () => {
        if (this._questLogPanel) {
          this._questLogPanel.open();
        }
      },
    });
    if (this._hudEl) {
      this._questTracker.attach(this._hudEl);
    }

    // Initialize quest system with party
    if (party) {
      questSystem.setParty(party);
    }

    // Auto-start main quest from scenario
    if (data && data.scenarioId) {
      this._startScenarioQuest(data.scenarioId);
    }

    // Expose for testing
    if (window.__debug) {
      window.__debug.travelSystem = travelSystem;
      window.__debug.worldTravelScene = this;
      window.__debug.inventorySystem = inventorySystem;
      window.__debug.inventoryPanel = this._inventoryPanel;
      window.__debug.questLogPanel = this._questLogPanel;
      window.__debug.questTracker = this._questTracker;
    }
  }

  exit() {
    debugLog.event('WorldTravelScene: exit');

    // Unsubscribe events
    this._unsubscribeEvents();

    // Remove HUD
    if (this._hudEl && this._hudEl.parentNode) {
      this._hudEl.parentNode.removeChild(this._hudEl);
    }
    this._hudEl = null;

    // Remove dialog
    this._dismissDialog();

    // Close inventory panel
    if (this._inventoryPanel) {
      this._inventoryPanel.close();
      this._inventoryPanel = null;
    }

    // Close quest log panel
    if (this._questLogPanel) {
      this._questLogPanel.close();
      this._questLogPanel = null;
    }

    // Detach quest tracker
    if (this._questTracker) {
      this._questTracker.detach();
      this._questTracker = null;
    }

    // Unsubscribe inventory hotkey
    if (this._eventHandlers.onInventory) {
      eventBus.off('action:inventory', this._eventHandlers.onInventory);
    }

    // Unsubscribe quest hotkey
    if (this._eventHandlers.onQuest) {
      eventBus.off('action:quest', this._eventHandlers.onQuest);
    }

    // Clean up parallax (including any in-progress transition)
    if (this._oldParallax) {
      this._oldParallax.destroy();
      this._oldParallax = null;
      this._transitionProgress = null;
    }
    if (this._parallax) {
      this._parallax.destroy();
      this._parallax = null;
    }

    // Clean up caravan
    if (this._caravanContainer) {
      if (this._caravanContainer.parent) {
        this._caravanContainer.parent.removeChild(this._caravanContainer);
      }
      this._caravanContainer.destroy({ children: true });
      this._caravanContainer = null;
    }

    focusManager.clear();
  }

  // ── Parallax Setup ──────────────────────────────────

  _buildParallax() {
    this._parallax = new ParallaxRenderer();
    this._parallax.resize(this._width, this._height);

    const w = TILE_WIDTH;
    const h = this._height;
    const groundY = h * (1 - GROUND_FRACTION);
    // Each layer draws 3 tiles so one full tile is always visible edge-to-edge
    const tiles = 3;

    // Layer 1: Sky (very slow scroll)
    const skyContainer = new Container();
    const sky = new Graphics();
    // Gradient sky fills entire above-ground area across all tiles
    for (let tile = 0; tile < tiles; tile++) {
      const ox = tile * w;
      sky.rect(ox, 0, w, groundY * 0.5);
      sky.fill(this._palette.sky[0]);
      sky.rect(ox, groundY * 0.5, w, groundY * 0.5);
      sky.fill(this._palette.sky[1]);
    }
    skyContainer.addChild(sky);

    // Add clouds spread across all tiles
    const clouds = new Graphics();
    for (let tile = 0; tile < tiles; tile++) {
      const ox = tile * w;
      for (let i = 0; i < 5; i++) {
        const cx = ox + (i * w / 5) + Math.random() * 80;
        const cy = 20 + Math.random() * (groundY * 0.35);
        const cw = 50 + Math.random() * 100;
        const ch = 12 + Math.random() * 18;
        clouds.ellipse(cx, cy, cw, ch);
        clouds.fill({ color: this._palette.clouds, alpha: 0.25 + Math.random() * 0.25 });
      }
    }
    skyContainer.addChild(clouds);

    this._parallax.addLayer('sky', 0.05, skyContainer, { width: w * tiles, tiled: true });

    // Layer 2: Mountains (mid-ground, slow scroll)
    const mtContainer = new Container();
    const mountains = new Graphics();

    for (let tile = 0; tile < tiles; tile++) {
      const ox = tile * w;
      for (let i = 0; i < 6; i++) {
        const mx = ox + i * (w / 5) + Math.random() * 60 - 30;
        const mw = 180 + Math.random() * 220;
        const mh = 60 + Math.random() * 140;
        const my = groundY - 10;

        mountains.moveTo(mx - mw / 2, my);
        mountains.lineTo(mx, my - mh);
        mountains.lineTo(mx + mw / 2, my);
        mountains.closePath();
        mountains.fill(this._palette.mountains[i % 2]);
      }
    }
    mtContainer.addChild(mountains);

    this._parallax.addLayer('mountains', 0.2, mtContainer, { width: w * tiles, tiled: true });

    // Layer 3: Trees / foreground features (moderate scroll)
    const treeContainer = new Container();
    const trees = new Graphics();

    for (let tile = 0; tile < tiles; tile++) {
      const ox = tile * w;
      for (let i = 0; i < 10; i++) {
        const tx = ox + i * (w / 9) + Math.random() * 40 - 20;
        const ty = groundY - 3;
        const tw = 18 + Math.random() * 24;
        const th = 35 + Math.random() * 65;
        const colorIdx = Math.floor(Math.random() * this._palette.trees.length);

        // Trunk
        trees.rect(tx - 4, ty - th * 0.3, 8, th * 0.3);
        trees.fill(0x553322);

        // Canopy (triangle)
        trees.moveTo(tx - tw, ty - th * 0.3);
        trees.lineTo(tx, ty - th);
        trees.lineTo(tx + tw, ty - th * 0.3);
        trees.closePath();
        trees.fill(this._palette.trees[colorIdx]);
      }
    }
    treeContainer.addChild(trees);

    this._parallax.addLayer('trees', 0.5, treeContainer, { width: w * tiles, tiled: true });

    // Layer 4: Ground (near, full scroll speed)
    const groundContainer = new Container();
    const ground = new Graphics();

    for (let tile = 0; tile < tiles; tile++) {
      const ox = tile * w;

      // Main ground — fills from ground line to bottom of viewport
      ground.rect(ox, groundY, w, h - groundY);
      ground.fill(this._palette.ground);

      // Path/road
      const pathY = groundY + (h - groundY) * 0.35;
      ground.rect(ox, pathY - PATH_HEIGHT / 2, w, PATH_HEIGHT);
      ground.fill(this._palette.path);

      // Path edges
      ground.rect(ox, pathY - PATH_HEIGHT / 2 - 2, w, 2);
      ground.fill({ color: 0x000000, alpha: 0.15 });
      ground.rect(ox, pathY + PATH_HEIGHT / 2, w, 2);
      ground.fill({ color: 0x000000, alpha: 0.15 });

      // Grass tufts above and below the path
      for (let g = 0; g < 25; g++) {
        const gx = ox + Math.random() * w;
        const gy = groundY + Math.random() * (h - groundY);
        if (gy > pathY - PATH_HEIGHT / 2 - 5 && gy < pathY + PATH_HEIGHT / 2 + 5) continue;

        ground.moveTo(gx - 3, gy);
        ground.lineTo(gx, gy - 8 - Math.random() * 6);
        ground.lineTo(gx + 3, gy);
        ground.closePath();
        ground.fill(this._palette.grass);
      }
    }
    groundContainer.addChild(ground);

    this._parallax.addLayer('ground', 1.0, groundContainer, { width: w * tiles, tiled: true });

    // Add parallax root to scene container
    this.container.addChild(this._parallax.root);
  }

  // ── Caravan Setup ───────────────────────────────────

  _buildCaravan() {
    this._caravanContainer = new Container();

    const groundY = this._height * (1 - GROUND_FRACTION);
    const pathY = groundY + (this._height - groundY) * 0.35;

    const party = travelSystem.party;

    // Vehicle in center (or default wagon if no vehicle set)
    const vehicle = party ? party.vehicle : null;
    const vehicleColor = vehicle ? parseInt(vehicle.color, 16) || 0x8b5a2b : 0x8b5a2b;

    const wagon = new Graphics();
    wagon.rect(-30, -30, 60, 25);
    wagon.fill(vehicleColor);
    // Wagon roof
    wagon.moveTo(-32, -30);
    wagon.lineTo(0, -50);
    wagon.lineTo(32, -30);
    wagon.closePath();
    wagon.fill(vehicleColor - 0x222200 > 0 ? vehicleColor - 0x222200 : 0x664422);
    // Wheels
    wagon.circle(-18, -2, 8);
    wagon.fill(0x443322);
    wagon.circle(18, -2, 8);
    wagon.fill(0x443322);
    // Wheel spokes
    wagon.circle(-18, -2, 3);
    wagon.fill(0x665544);
    wagon.circle(18, -2, 3);
    wagon.fill(0x665544);

    // Vehicle label (small indicator)
    if (vehicle) {
      wagon.rect(-25, -48, 50, 12);
      wagon.fill({ color: 0x000000, alpha: 0.3 });
    }

    this._caravanContainer.addChild(wagon);

    // Party member figure(s) walking beside the wagon
    const memberCount = party ? Math.min(party.members.length, 4) : 1;

    for (let i = 0; i < memberCount; i++) {
      const figure = new Graphics();
      const fx = -50 - i * 20;

      // Body (simple colored rectangle)
      const colors = [0x4466aa, 0xaa4444, 0x44aa44, 0xaa8844];
      figure.rect(fx - 5, -24, 10, 18);
      figure.fill(colors[i % colors.length]);

      // Head
      figure.circle(fx, -30, 6);
      figure.fill(0xddbbaa);

      // Legs
      figure.rect(fx - 4, -6, 3, 8);
      figure.fill(0x333333);
      figure.rect(fx + 1, -6, 3, 8);
      figure.fill(0x333333);

      this._caravanContainer.addChild(figure);

      // Draw companion walking alongside owner (smaller teal figure)
      if (party && party.companions) {
        const companionForMember = party.companions.find(
          c => c.ownerId === party.members[i]?.id
        );
        if (companionForMember) {
          const compFigure = new Graphics();
          const cfx = fx;
          const cfy = 8; // Below the owner

          // Companion body (smaller, teal colored)
          compFigure.rect(cfx - 4, cfy - 16, 8, 12);
          compFigure.fill(0x44aaaa);

          // Companion head (smaller)
          compFigure.circle(cfx, cfy - 20, 4);
          compFigure.fill(0x88dddd);

          // Companion legs
          compFigure.rect(cfx - 3, cfy - 4, 2, 6);
          compFigure.fill(0x333333);
          compFigure.rect(cfx + 1, cfy - 4, 2, 6);
          compFigure.fill(0x333333);

          this._caravanContainer.addChild(compFigure);
        }
      }
    }

    // Pack animals trailing behind the wagon
    if (party && party.packAnimals) {
      let animalOffset = 40;
      for (let i = 0; i < party.packAnimals.length; i++) {
        const animal = party.packAnimals[i];
        if (!animal) continue;

        const animalGfx = new Graphics();
        const ax = animalOffset + i * 30;
        const animalColor = parseInt(animal.color, 16) || 0x8b7355;

        // Animal body (rectangle, slightly larger than character)
        animalGfx.rect(ax - 8, -20, 16, 14);
        animalGfx.fill(animalColor);

        // Animal head
        animalGfx.circle(ax - 10, -18, 5);
        animalGfx.fill(animalColor);

        // Animal legs
        animalGfx.rect(ax - 6, -6, 3, 8);
        animalGfx.fill(animalColor - 0x111111 > 0 ? animalColor - 0x111111 : 0x333322);
        animalGfx.rect(ax + 3, -6, 3, 8);
        animalGfx.fill(animalColor - 0x111111 > 0 ? animalColor - 0x111111 : 0x333322);

        this._caravanContainer.addChild(animalGfx);
      }
    }

    // Position caravan on the path
    this._caravanContainer.x = this._width * 0.35;
    this._caravanContainer.y = pathY;

    this.container.addChild(this._caravanContainer);
  }

  // ── HUD Setup (HTML/CSS) ────────────────────────────

  _buildHUD() {
    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this._hudEl = document.createElement('div');
    this._hudEl.id = 'travel-hud';
    this._hudEl.className = 'travel-hud';
    this._hudEl.setAttribute('data-scene', 'WorldTravel');

    // Top-left: Region info
    const regionInfo = document.createElement('div');
    regionInfo.className = 'travel-hud-region';
    regionInfo.id = 'travel-region-info';
    regionInfo.innerHTML = `
      <div class="travel-region-name" id="travel-region-name">Loading...</div>
      <div class="travel-region-level" id="travel-region-level"></div>
    `;
    this._hudEl.appendChild(regionInfo);

    // Top-right: Day/time
    const timeInfo = document.createElement('div');
    timeInfo.className = 'travel-hud-time';
    timeInfo.id = 'travel-time-info';
    timeInfo.innerHTML = `
      <div class="travel-day" id="travel-day">Day 1</div>
      <div class="travel-time-of-day" id="travel-time-of-day">Morning</div>
    `;
    this._hudEl.appendChild(timeInfo);

    // Bottom bar
    const bottomBar = document.createElement('div');
    bottomBar.className = 'travel-hud-bottom';

    // Speed button
    const speedBtn = document.createElement('button');
    speedBtn.className = 'travel-speed-btn';
    speedBtn.id = 'travel-speed-btn';
    speedBtn.setAttribute('data-focusable', 'true');
    speedBtn.textContent = 'Normal';
    speedBtn.addEventListener('click', () => {
      const newSpeed = travelSystem.toggleSpeed();
      speedBtn.textContent = newSpeed === 1 ? 'Normal' : 'Fast (x2)';
    });
    bottomBar.appendChild(speedBtn);

    // Party status summary
    const partyStatus = document.createElement('div');
    partyStatus.className = 'travel-party-status';
    partyStatus.id = 'travel-party-status';
    partyStatus.textContent = '';
    bottomBar.appendChild(partyStatus);

    this._hudEl.appendChild(bottomBar);

    uiRoot.appendChild(this._hudEl);
  }

  _updateHUD() {
    const region = travelSystem.currentRegion;
    if (!region) return;

    // Region name
    const regionNameEl = document.getElementById('travel-region-name');
    if (regionNameEl) regionNameEl.textContent = region.name;

    // Region level
    const regionLevelEl = document.getElementById('travel-region-level');
    if (regionLevelEl) regionLevelEl.textContent = `Lv ${region.levelRange[0]}-${region.levelRange[1]}`;

    // Day
    const dayEl = document.getElementById('travel-day');
    if (dayEl) dayEl.textContent = `Day ${travelSystem.day}`;

    // Time of day
    const todEl = document.getElementById('travel-time-of-day');
    if (todEl) todEl.textContent = travelSystem.getTimeOfDay();

    // Party status
    const partyEl = document.getElementById('travel-party-status');
    if (partyEl && travelSystem.party) {
      const members = travelSystem.party.members;
      const summaries = members.map(m => `${m.name}: ${m.hp}/${m.maxHp} HP`);
      partyEl.textContent = summaries.join('  |  ');
    }

    // Speed button
    const speedBtn = document.getElementById('travel-speed-btn');
    if (speedBtn) {
      speedBtn.textContent = travelSystem.speedMultiplier === 1 ? 'Normal' : 'Fast (x2)';
    }
  }

  // ── Dialog System ───────────────────────────────────

  _showRegionTransitionDialog(connections) {
    this._dismissDialog();

    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    const region = travelSystem.currentRegion;

    this._dialogEl = document.createElement('div');
    this._dialogEl.className = 'travel-dialog';
    this._dialogEl.id = 'travel-region-dialog';
    this._dialogEl.setAttribute('role', 'dialog');
    this._dialogEl.setAttribute('aria-label', 'Region Transition');

    const title = document.createElement('h3');
    title.className = 'travel-dialog-title';
    title.textContent = `Leaving ${region ? region.name : 'Unknown'}`;
    this._dialogEl.appendChild(title);

    const desc = document.createElement('p');
    desc.className = 'travel-dialog-desc';
    desc.textContent = 'Choose your next destination:';
    this._dialogEl.appendChild(desc);

    const btnContainer = document.createElement('div');
    btnContainer.className = 'travel-dialog-buttons';

    for (const connId of connections) {
      const connRegion = dataStore.getById('regions', connId);
      if (!connRegion) continue;

      const btn = document.createElement('button');
      btn.className = 'travel-dialog-btn';
      btn.setAttribute('data-focusable', 'true');
      btn.setAttribute('data-region-id', connId);
      btn.innerHTML = `
        <span class="travel-dialog-btn-name">${connRegion.name}</span>
        <span class="travel-dialog-btn-level">Lv ${connRegion.levelRange[0]}-${connRegion.levelRange[1]}</span>
      `;
      btn.addEventListener('click', () => {
        this._dismissDialog();
        travelSystem.changeRegion(connId);
        travelSystem.resume();
        // Update palette for new biome
        const newRegion = travelSystem.currentRegion;
        if (newRegion) {
          this._palette = BIOME_PALETTES[newRegion.biome] || DEFAULT_PALETTE;
          this._rebuildParallax();
        }
        this._updateHUD();
      });
      btnContainer.appendChild(btn);
    }

    this._dialogEl.appendChild(btnContainer);

    // Stay button
    const stayBtn = document.createElement('button');
    stayBtn.className = 'travel-dialog-btn travel-dialog-btn-cancel';
    stayBtn.setAttribute('data-focusable', 'true');
    stayBtn.textContent = 'Stay in Region';
    stayBtn.addEventListener('click', () => {
      this._dismissDialog();
      travelSystem.position = 0;
      travelSystem.resume();
    });
    btnContainer.appendChild(stayBtn);

    uiRoot.appendChild(this._dialogEl);

    // Push focus group
    focusManager.pushGroup('region-transition', this._dialogEl);
  }

  _showEncounterDialog(encounter) {
    this._dismissDialog();

    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this._dialogEl = document.createElement('div');
    this._dialogEl.className = 'travel-dialog';
    this._dialogEl.id = 'travel-encounter-dialog';
    this._dialogEl.setAttribute('role', 'dialog');
    this._dialogEl.setAttribute('aria-label', 'Encounter');

    const title = document.createElement('h3');
    title.className = 'travel-dialog-title';
    title.textContent = 'Encounter!';
    this._dialogEl.appendChild(title);

    // Describe the encounter
    const desc = document.createElement('p');
    desc.className = 'travel-dialog-desc';

    if (encounter.enemies && encounter.enemies.length > 0) {
      // Group enemies by name
      const counts = {};
      for (const e of encounter.enemies) {
        counts[e.name] = (counts[e.name] || 0) + 1;
      }
      const parts = Object.entries(counts).map(([name, count]) =>
        count > 1 ? `${name} x${count}` : name
      );
      desc.textContent = `You encounter: ${parts.join(', ')}!`;
    } else {
      desc.textContent = 'You encounter something in the distance...';
    }
    this._dialogEl.appendChild(desc);

    const btnContainer = document.createElement('div');
    btnContainer.className = 'travel-dialog-buttons';

    // Fight button (placeholder)
    const fightBtn = document.createElement('button');
    fightBtn.className = 'travel-dialog-btn travel-dialog-btn-fight';
    fightBtn.setAttribute('data-focusable', 'true');
    fightBtn.textContent = 'Fight';
    fightBtn.addEventListener('click', () => {
      this._dismissDialog();
      debugLog.info('WorldTravel: Fight initiated, transitioning to Combat');
      sceneManager.transition('Combat', {
        party: travelSystem.party,
        enemies: encounter.enemies.map(e => ({ id: e.id, level: e.level || 1 })),
        returnScene: 'WorldTravel',
        returnData: { regionId: travelSystem.currentRegion?.id },
      });
    });
    btnContainer.appendChild(fightBtn);

    // Flee button
    const fleeBtn = document.createElement('button');
    fleeBtn.className = 'travel-dialog-btn travel-dialog-btn-cancel';
    fleeBtn.setAttribute('data-focusable', 'true');
    fleeBtn.textContent = 'Flee';
    fleeBtn.addEventListener('click', () => {
      this._dismissDialog();
      travelSystem.resume();
      debugLog.info('WorldTravel: Fled from encounter');
    });
    btnContainer.appendChild(fleeBtn);

    this._dialogEl.appendChild(btnContainer);

    uiRoot.appendChild(this._dialogEl);

    // Push focus group
    focusManager.pushGroup('encounter', this._dialogEl);
  }

  _dismissDialog() {
    if (this._dialogEl) {
      // Pop focus group if it's for one of our dialogs
      const topGroup = focusManager._currentGroup?.call
        ? focusManager._currentGroup()
        : null;
      if (topGroup && (topGroup.name === 'region-transition' || topGroup.name === 'encounter')) {
        focusManager.popGroup();
      }

      if (this._dialogEl.parentNode) {
        this._dialogEl.parentNode.removeChild(this._dialogEl);
      }
      this._dialogEl = null;
    }
  }

  // ── Event Subscriptions ─────────────────────────────

  _subscribeEvents() {
    this._eventHandlers.onNode = () => {
      this._updateHUD();
    };
    this._eventHandlers.onDayAdvance = () => {
      this._updateHUD();
    };
    this._eventHandlers.onEncounter = (encounter) => {
      this._showEncounterDialog(encounter);
    };
    this._eventHandlers.onRegionEdge = (data) => {
      this._showRegionTransitionDialog(data.connections);
    };
    this._eventHandlers.onRegionChange = () => {
      this._updateHUD();
    };
    this._eventHandlers.onTimeChanged = () => {
      this._updateHUD();
    };

    eventBus.on('travel:node', this._eventHandlers.onNode);
    eventBus.on('travel:day-advance', this._eventHandlers.onDayAdvance);
    eventBus.on('travel:encounter', this._eventHandlers.onEncounter);
    eventBus.on('travel:region-edge', this._eventHandlers.onRegionEdge);
    eventBus.on('travel:region-change', this._eventHandlers.onRegionChange);
    eventBus.on('travel:time-changed', this._eventHandlers.onTimeChanged);
  }

  _unsubscribeEvents() {
    if (this._eventHandlers.onNode) {
      eventBus.off('travel:node', this._eventHandlers.onNode);
    }
    if (this._eventHandlers.onDayAdvance) {
      eventBus.off('travel:day-advance', this._eventHandlers.onDayAdvance);
    }
    if (this._eventHandlers.onEncounter) {
      eventBus.off('travel:encounter', this._eventHandlers.onEncounter);
    }
    if (this._eventHandlers.onRegionEdge) {
      eventBus.off('travel:region-edge', this._eventHandlers.onRegionEdge);
    }
    if (this._eventHandlers.onRegionChange) {
      eventBus.off('travel:region-change', this._eventHandlers.onRegionChange);
    }
    if (this._eventHandlers.onTimeChanged) {
      eventBus.off('travel:time-changed', this._eventHandlers.onTimeChanged);
    }
    this._eventHandlers = {};
  }

  // ── Debug Commands ──────────────────────────────────

  _registerDebugCommands() {
    const dc = window.__debug?.console;
    if (!dc) return;

    // Override teleport command with working version
    dc.registerCommand('teleport', (args) => {
      const regionId = args[0];
      if (!regionId) return 'Usage: teleport <region_id>';

      const region = dataStore.getById('regions', regionId);
      if (!region) return `Region not found: ${regionId}`;

      travelSystem.changeRegion(regionId);
      travelSystem.resume();

      // Update visuals
      this._palette = BIOME_PALETTES[region.biome] || DEFAULT_PALETTE;
      this._rebuildParallax();
      this._updateHUD();

      return `Teleported to ${region.name}`;
    }, 'Teleport to region: teleport <region_id>');

    // Override settime command with working version
    dc.registerCommand('settime', (args) => {
      const nodeIdx = parseInt(args[0], 10);
      if (isNaN(nodeIdx)) return 'Usage: settime <0-4> (0=Morning, 1-2=Afternoon, 3=Evening, 4=Night)';

      travelSystem.setTimeOfDay(nodeIdx);
      this._updateHUD();

      return `Time set to ${travelSystem.getTimeOfDay()} (node ${travelSystem.nodeInDay})`;
    }, 'Set time of day: settime <0-4>');

    // Trigger encounter
    dc.registerCommand('triggerencounter', () => {
      const encounter = travelSystem.triggerEncounter();
      return `Encounter triggered: ${encounter.enemies?.map(e => e.name).join(', ') || 'empty'}`;
    }, 'Trigger a random encounter');

    // Force region transition
    dc.registerCommand('triggerregion', () => {
      const region = travelSystem.currentRegion;
      if (!region) return 'No current region';

      travelSystem.paused = true;
      this._showRegionTransitionDialog(region.connections);
      return `Region transition dialog shown. Connections: ${region.connections.join(', ')}`;
    }, 'Show region transition dialog');

    // Expose on window.__debug.commands
    if (window.__debug) {
      window.__debug.commands.teleport = (args) => {
        const regionId = typeof args === 'string' ? args : args[0];
        travelSystem.changeRegion(regionId);
        travelSystem.resume();
        this._palette = BIOME_PALETTES[travelSystem.currentRegion?.biome] || DEFAULT_PALETTE;
        this._rebuildParallax();
        this._updateHUD();
      };
      window.__debug.commands.settime = (args) => {
        const nodeIdx = typeof args === 'number' ? args : parseInt(args[0] || args, 10);
        travelSystem.setTimeOfDay(nodeIdx);
        this._updateHUD();
      };
      window.__debug.commands.triggerencounter = () => {
        return travelSystem.triggerEncounter();
      };
      window.__debug.commands.triggerregion = () => {
        const region = travelSystem.currentRegion;
        if (region) {
          travelSystem.paused = true;
          this._showRegionTransitionDialog(region.connections);
        }
      };
      window.__debug.commands.startdialog = (args) => {
        const dialogId = typeof args === 'string' ? args : (args && args[0]);
        if (!dialogId) return 'Usage: startdialog <dialog_id>';
        import('../systems/DialogSystem.js').then(mod => {
          const ds = mod.default;
          if (!ds.getDialog(dialogId)) return;
          sceneManager.transition('Dialog', {
            dialogId,
            party: travelSystem.party,
            returnScene: 'WorldTravel',
            returnData: {},
          });
        });
        return `Starting dialog: ${dialogId}`;
      };
    }

    // Register startdialog as a console command
    dc.registerCommand('startdialog', (args) => {
      const dialogId = args[0];
      if (!dialogId) return 'Usage: startdialog <dialog_id>';
      // Dynamic import to avoid circular dependency
      import('../systems/DialogSystem.js').then(mod => {
        const ds = mod.default;
        if (!ds.getDialog(dialogId)) {
          debugLog.warn(`Dialog not found: ${dialogId}`);
          return;
        }
        sceneManager.transition('Dialog', {
          dialogId,
          party: travelSystem.party,
          returnScene: 'WorldTravel',
          returnData: {},
        });
      });
      return `Starting dialog: ${dialogId}`;
    }, 'Start a dialog: startdialog <dialog_id>');

    // Enter town
    dc.registerCommand('entertown', (args) => {
      const townId = args[0];
      if (!townId) return 'Usage: entertown <town_id>';

      import('../systems/TownSystem.js').then(mod => {
        const ts = mod.default;
        const town = ts.getTown(townId);
        if (!town) {
          const allTowns = ts.getAllTowns().map(t => t.id).join(', ');
          debugLog.warn(`Town not found: ${townId}. Available: ${allTowns}`);
          return;
        }
        sceneManager.transition('Town', {
          townId,
          party: travelSystem.party,
          returnScene: 'WorldTravel',
          returnData: { regionId: travelSystem.currentRegion?.id },
        });
      });
      return `Entering town: ${townId}`;
    }, 'Enter a town: entertown <town_id>');

    if (window.__debug) {
      window.__debug.commands.entertown = (args) => {
        const townId = typeof args === 'string' ? args : (args && args[0]);
        if (!townId) return;
        import('../systems/TownSystem.js').then(mod => {
          const ts = mod.default;
          const town = ts.getTown(townId);
          if (!town) return;
          sceneManager.transition('Town', {
            townId,
            party: travelSystem.party,
            returnScene: 'WorldTravel',
            returnData: { regionId: travelSystem.currentRegion?.id },
          });
        });
      };

      // Quest debug commands
      window.__debug.commands.startquest = (args) => {
        const questId = typeof args === 'string' ? args : (args && args[0]);
        if (!questId) return 'Usage: startquest <quest_id>';
        const result = questSystem.startQuest(questId);
        return result ? `Started quest: ${questId}` : `Failed to start quest: ${questId}`;
      };
      window.__debug.commands.completequest = (args) => {
        const questId = typeof args === 'string' ? args : (args && args[0]);
        if (!questId) return 'Usage: completequest <quest_id>';
        const result = questSystem.completeQuest(questId);
        return result ? `Completed quest: ${questId}` : `Failed to complete quest: ${questId}`;
      };
      window.__debug.commands.listquests = () => {
        const active = questSystem.getActiveQuests();
        const completed = questSystem.getCompletedQuests();
        return `Active: ${active.map(q => q.questId).join(', ') || 'none'}\nCompleted: ${completed.map(q => q.questId).join(', ') || 'none'}`;
      };
    }

    // Register console commands
    dc.registerCommand('startquest', (args) => {
      const questId = args[0];
      if (!questId) return 'Usage: startquest <quest_id>';
      const result = questSystem.startQuest(questId);
      return result ? `Started quest: ${questId}` : `Failed to start quest: ${questId}`;
    }, 'Start a quest: startquest <quest_id>');

    dc.registerCommand('completequest', (args) => {
      const questId = args[0];
      if (!questId) return 'Usage: completequest <quest_id>';
      const result = questSystem.completeQuest(questId);
      return result ? `Completed quest: ${questId}` : `Failed to complete quest: ${questId}`;
    }, 'Complete a quest: completequest <quest_id>');

    dc.registerCommand('listquests', () => {
      const active = questSystem.getActiveQuests();
      const completed = questSystem.getCompletedQuests();
      return `Active: ${active.map(q => q.questId).join(', ') || 'none'}\nCompleted: ${completed.map(q => q.questId).join(', ') || 'none'}`;
    }, 'List all quests');
  }

  // ── Scenario Quest Starter ─────────────────────────

  /**
   * Start the main quest associated with a starting scenario.
   * @param {string} scenarioId
   */
  _startScenarioQuest(scenarioId) {
    const scenarioQuestMap = {
      exiled_knight: 'main_exiled_knight',
      wandering_scholar: 'main_wandering_scholar',
      merchants_heir: 'main_merchants_heir',
      apprentice: 'main_apprentice',
      survivor: 'main_survivor',
    };

    const questId = scenarioQuestMap[scenarioId];
    if (questId && !questSystem.isActive(questId) && !questSystem.isCompleted(questId)) {
      questSystem.startQuest(questId);
      debugLog.info(`WorldTravel: Started scenario quest "${questId}" for scenario "${scenarioId}"`);
    }
  }

  // ── Rebuild with seamless crossfade ─────────────────

  /** @type {ParallaxRenderer|null} Old parallax being faded out. */
  _oldParallax = null;
  /** Transition progress 0-1 (null = no transition). */
  _transitionProgress = null;
  /** Transition duration in seconds. */
  _transitionDuration = 2.5;

  _rebuildParallax() {
    // Keep old parallax alive for crossfade
    if (this._parallax) {
      // Clean up any previous old parallax still fading
      if (this._oldParallax) {
        if (this._oldParallax.root.parent) {
          this.container.removeChild(this._oldParallax.root);
        }
        this._oldParallax.destroy();
      }
      this._oldParallax = this._parallax;
      this._oldParallax.root.alpha = 1;
      this._transitionProgress = 0;
      this._parallax = null;
    }

    // Remove and re-add caravan so it stays on top of new layers
    if (this._caravanContainer && this._caravanContainer.parent) {
      this.container.removeChild(this._caravanContainer);
    }

    this._buildParallax();
    this._parallax.root.alpha = 0; // Start invisible, will fade in

    // Re-add caravan on top
    if (this._caravanContainer) {
      this.container.addChild(this._caravanContainer);
    }

    // Reset camera
    this._cameraX = travelSystem.position * 100;
  }

  // ── Update Loop ─────────────────────────────────────

  update(dt) {
    // Check for shift key to toggle fast speed while held
    if (inputManager.isPressed('Shift') || inputManager.isPressed('ShiftLeft') || inputManager.isPressed('ShiftRight')) {
      if (travelSystem.speedMultiplier !== 2) {
        travelSystem.setSpeed(2);
        const speedBtn = document.getElementById('travel-speed-btn');
        if (speedBtn) speedBtn.textContent = 'Fast (x2)';
      }
    } else if (travelSystem.speedMultiplier === 2) {
      // Only reset to normal if speed was set by shift (not by button)
      // We check if shift was just released
    }

    // Advance travel
    travelSystem.advance(dt);

    // Update camera based on travel position
    this._cameraX = travelSystem.position * 100;

    // Handle region crossfade transition
    if (this._transitionProgress !== null && this._oldParallax) {
      this._transitionProgress += dt / this._transitionDuration;
      if (this._transitionProgress >= 1) {
        // Transition complete — remove old parallax
        this._transitionProgress = null;
        if (this._oldParallax.root.parent) {
          this.container.removeChild(this._oldParallax.root);
        }
        this._oldParallax.destroy();
        this._oldParallax = null;
        if (this._parallax) this._parallax.root.alpha = 1;
      } else {
        // Smooth crossfade
        const t = this._transitionProgress;
        const ease = t * t * (3 - 2 * t); // smoothstep
        this._oldParallax.root.alpha = 1 - ease;
        if (this._parallax) this._parallax.root.alpha = ease;
        // Keep old parallax scrolling too
        this._oldParallax.update(this._cameraX);
      }
    }

    // Update parallax
    if (this._parallax) {
      this._parallax.update(this._cameraX);

      // Update day/night tint
      const tint = travelSystem.getDayNightTint();
      this._parallax.lerpTint(tint.color, tint.alpha, 2.0, dt);
    }

    // Simple caravan bob animation
    if (this._caravanContainer && travelSystem.traveling && !travelSystem.paused) {
      this._caravanContainer.y += Math.sin(performance.now() * 0.004) * 0.15;
    }
  }

  // ── Resize ──────────────────────────────────────────

  resize(w, h) {
    this._width = w;
    this._height = h;

    if (this._parallax) {
      this._parallax.resize(w, h);
      // Reposition layers after resize so they don't appear off-screen
      this._parallax.update(this._cameraX);
    }

    // Reposition caravan
    if (this._caravanContainer) {
      const groundY = h * (1 - GROUND_FRACTION);
      const pathY = groundY + (h - groundY) * 0.35;
      this._caravanContainer.x = w * 0.35;
      this._caravanContainer.y = pathY;
    }
  }
}
